import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import type { ErrorReporter } from './types';

interface RootFallbackProps {
  onReset: () => void;
}

function RootFallback({ onReset }: RootFallbackProps) {
  const { t } = useTranslation('core');
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-red-500" />
        <h1 className="text-xl font-bold mb-2">{t('errorBoundary.title')}</h1>
        <p className="text-sm text-muted-foreground mb-6">{t('errorBoundary.rootDescription')}</p>
        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={onReset}>
            {t('errorBoundary.retry')}
          </Button>
          <Button onClick={() => window.location.reload()}>{t('errorBoundary.reload')}</Button>
        </div>
      </div>
    </div>
  );
}

interface Props {
  children: React.ReactNode;
  reporter?: ErrorReporter;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class RootErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[RootErrorBoundary]', error, info);
    this.props.reporter?.report(error, info);
  }

  render() {
    if (this.state.hasError) {
      return <RootFallback onReset={() => this.setState({ hasError: false, error: null })} />;
    }
    return this.props.children;
  }
}
